import { useState, useEffect, useRef } from "react";
import { Loader2, Search } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

export const ErpItemSearch = ({ onSelect, placeholder }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/erp-items/search", { params: { q } });
        setResults(data);
        setOpen(true);
      } catch (e) { console.error("ERP item search failed:", e); } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const pick = (it) => { onSelect(it); setQ(""); setResults([]); setOpen(false); };

  return (
    <div ref={boxRef} className="relative flex-1 min-w-[260px] max-w-xl">
      <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
      <Input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => results.length && setOpen(true)}
             placeholder={placeholder || "Type stock code or product description…"}
             className="h-9 pl-9 pr-8" data-testid="erp-item-search-input" />
      {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />}
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-80 overflow-y-auto" data-testid="erp-item-suggestions">
          {results.map((it) => (
            <button key={it.item_code} onClick={() => pick(it)}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
                    data-testid={`erp-item-option-${it.item_code}`}>
              <span className="font-mono text-[11px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded mr-2">{it.stock_code}</span>
              <span className="text-sm text-slate-700">{it.description}</span>
              <span className="block text-[11px] text-slate-400 mt-0.5">{it.category} · {it.uom} · Item #{it.item_code}{it.dtp_price ? <span className="text-emerald-600 font-semibold"> · DTP {it.dtp_price}</span> : ""}</span>
            </button>
          ))}
          {results.length === 0 && !loading && <p className="px-3 py-3 text-sm text-slate-400">No matching product codes.</p>}
        </div>
      )}
    </div>
  );
};
